import { useEffect, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import Spinner from '../../components/Spinner';
import { SOA_OPTIONS } from '../../utils/soa';

const campi = [
  { name: 'ragione_sociale', label: 'Ragione sociale', required: true },
  { name: 'indirizzo', label: 'Indirizzo' },
  { name: 'citta', label: 'Città' },
  { name: 'cap', label: 'CAP' },
  { name: 'provincia', label: 'Provincia' },
  { name: 'partita_iva', label: 'Partita IVA' },
  { name: 'codice_fiscale', label: 'Codice fiscale' },
  { name: 'camera_commercio_di', label: 'CCIAA di' },
  { name: 'iscrizione_cciaa', label: 'N° iscrizione CCIAA' },
  { name: 'data_iscrizione_cciaa', label: 'Data iscrizione CCIAA', type: 'date' },
  { name: 'email', label: 'Email', type: 'email' },
  { name: 'data_attestazione', label: 'Data attestazione', type: 'date' },
  { name: 'soa', label: 'SOA', options: SOA_OPTIONS },
  { name: 'codice_accesso', label: 'Codice di accesso' },
  { name: 'data_contratto', label: 'Data contratto', type: 'date' },
  { name: 'percentuale', label: 'Percentuale', type: 'number' },
];

const checkboxes = [];

function toDateInput(val) {
  if (!val) return '';
  return val.substring(0, 10);
}

export default function FormImpresa() {
  const { id } = useParams();
  const isNuova = id === 'nuova';
  const navigate = useNavigate();
  const location = useLocation();
  const [form, setForm] = useState({ ragione_sociale: '' });
  const [loading, setLoading] = useState(!isNuova);
  const [saving, setSaving] = useState(false);
  const [errore, setErrore] = useState('');
  const [tipiRichiesta, setTipiRichiesta] = useState([]);

  useEffect(() => {
    axios.get('/api/richieste').then(r => setTipiRichiesta(r.data));
  }, []);

  useEffect(() => {
    if (isNuova) {
      const prefill = location.state?.prefill;
      if (prefill) {
        setForm({
          ragione_sociale:      prefill.ragione_sociale || '',
          indirizzo:            prefill.indirizzo || '',
          citta:                prefill.citta || '',
          cap:                  prefill.cap || '',
          provincia:            prefill.provincia || '',
          partita_iva:          prefill.partita_iva || '',
          codice_fiscale:       prefill.codice_fiscale || '',
          camera_commercio_di:  prefill.camera_commercio_di || '',
          iscrizione_cciaa:     prefill.iscrizione_cciaa || '',
          data_iscrizione_cciaa: prefill.data_iscrizione_cciaa ? toDateInput(prefill.data_iscrizione_cciaa) : '',
          email:                prefill.email || '',
        });
      }
    } else {
      axios.get(`/api/imprese/${id}`)
        .then(r => {
          const d = r.data;
          ['data_iscrizione_cciaa', 'data_attestazione', 'data_contratto'].forEach(k => {
            if (d[k]) d[k] = toDateInput(d[k]);
          });
          setForm(d);
        })
        .finally(() => setLoading(false));
    }
  }, [id, isNuova]);

  function handleChange(e) {
    const { name, value, type, checked } = e.target;
    setForm(f => ({ ...f, [name]: type === 'checkbox' ? checked : value }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setSaving(true);
    setErrore('');
    try {
      if (isNuova) {
        const r = await axios.post('/api/imprese', form);
        navigate(`/admin/imprese/${r.data.id}`);
      } else {
        await axios.put(`/api/imprese/${id}`, form);
        navigate(`/admin/imprese/${id}`);
      }
    } catch (err) {
      setErrore(err.response?.data?.error || 'Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <Spinner />;

  return (
    <div>
      <div className="flex items-center gap-3 mb-6">
        <button onClick={() => navigate(-1)} className="text-[#2E6096] hover:underline text-sm">← Indietro</button>
        <h1 className="text-2xl font-bold text-[#1A3A5C]">
          {isNuova ? 'Nuova impresa' : 'Modifica impresa'}
        </h1>
      </div>

      <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {campi.map(campo => (
            <div key={campo.name} className={campo.name === 'ragione_sociale' ? 'md:col-span-2' : ''}>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {campo.label}{campo.required && <span className="text-red-500 ml-1">*</span>}
              </label>
              {campo.options ? (
                <select
                  name={campo.name}
                  value={form[campo.name] || ''}
                  onChange={handleChange}
                  required={campo.required}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#2E6096]"
                >
                  <option value="">— Seleziona —</option>
                  {campo.options.map(o => <option key={o} value={o}>{o}</option>)}
                  {/* Valore storico non ancora normalizzato: mostrarlo invece di far apparire un'altra opzione */}
                  {form[campo.name] && !campo.options.includes(form[campo.name]) && (
                    <option value={form[campo.name]}>{form[campo.name]}</option>
                  )}
                </select>
              ) : (
                <input
                  type={campo.type || 'text'}
                  name={campo.name}
                  value={form[campo.name] || ''}
                  onChange={handleChange}
                  required={campo.required}
                  step={campo.type === 'number' ? 'any' : undefined}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#2E6096]"
                />
              )}
            </div>
          ))}
        </div>

        {/* Tipo pratica */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Tipo pratica</label>
            <select
              name="richiesta"
              value={form.richiesta || ''}
              onChange={handleChange}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#2E6096]"
            >
              <option value="">— Seleziona —</option>
              {tipiRichiesta.map(r => (
                <option key={r.id} value={r.descrizione}>{r.descrizione}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">N° richiesta</label>
            <input
              type="text"
              name="numero_richiesta"
              value={form.numero_richiesta || ''}
              onChange={handleChange}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#2E6096]"
            />
          </div>
        </div>

        <div className="mt-4 flex flex-wrap gap-4">
          {checkboxes.map(cb => (
            <label key={cb.name} className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
              <input
                type="checkbox"
                name={cb.name}
                checked={!!form[cb.name]}
                onChange={handleChange}
                className="w-4 h-4 accent-[#1A3A5C]"
              />
              {cb.label}
            </label>
          ))}
        </div>

        {errore && <p className="mt-4 text-red-600 text-sm">{errore}</p>}

        <div className="mt-6 flex gap-3">
          <button
            type="submit"
            disabled={saving}
            className="bg-[#1A3A5C] text-white px-6 py-2 rounded-lg font-medium hover:bg-[#2E6096] transition disabled:opacity-50"
          >
            {saving ? 'Salvataggio...' : 'Salva'}
          </button>
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="border border-gray-300 px-6 py-2 rounded-lg text-sm hover:bg-gray-50 transition"
          >
            Annulla
          </button>
        </div>
      </form>
    </div>
  );
}
